import { Link } from "react-router-dom";
import ApeCarousel from "../components/ApeCarousel";
import EarningsSimulator from "../components/EarningsSimulator";
import TrustBar from "../components/TrustBar";
import TokenomicsDiagram from "../components/TokenomicsDiagram";
import { getCodeApeXUrl, getDisplayCapeTokenAddress, getDisplayNftAddress, getExplorerAddressUrl, getUniswapTradeUrl, shortAddress } from "../utils/launchLinks";
import { envString } from "../utils/config";

const SITE_URL = envString("VITE_CODE_APE_SITE_URL") ?? "https://code-ape.com";
const SKILL_URL = envString("VITE_CODE_APE_SKILL_URL") ?? "https://code-ape.com/skill";
const CANONICAL_SKILL_URL = `${SITE_URL.replace(/\/+$/, "")}/skill/RUN.md`;
const CODEX_PROMPT = `Codex, read ${CANONICAL_SKILL_URL} and summon my CODE APE. Use the compact cinematic style lock. Do not copy references or generate flat vector/sticker art.`;

const STATS = [
  { value: "8,000", label: "AI-signed Apes" },
  { value: "0.003 ETH", label: "Mint price" },
  { value: "1% WETH", label: "Earned per swap" },
  { value: "5,000+", label: "CAPE if you burn" },
  { value: "Auto", label: "LP locks into UNCX after launch" },
  { value: "Short link", label: "Mint handoff" }
];

const FIRSTS = [
  {
    index: "01",
    pillar: "HOOK",
    title: "Uniswap v4 Hook routed to NFT holders, not LPs",
    body: "For years, published v4 hooks mostly served DEX goals: dynamic fees, limit orders, and routing games. CODE APE treats Uniswap v4 as a settlement layer for NFT economics. Every official swap can route WETH fee flow to active NFTs through a MasterChef-style accumulator. The hook is not a DEX optimizer. It is the cashier for the colony.",
    caveat: "Fees flow only when trading happens. This is not advertised APY."
  },
  {
    index: "02",
    pillar: "NFT",
    title: "AI creator identity, cryptographically fossilized",
    body: "NFT history kept adding meaning to ownership: on-chain receipts, IP, community, aesthetic, brand, and token loops. CODE APE adds the creator path. Skill version, image hash, and handoff hash make it provable later that a specific AI workflow drew exactly this Ape for exactly this wallet.",
    caveat: "End-to-end Skill + image + handoff hashing is part of the artifact."
  },
  {
    index: "03",
    pillar: "AI",
    title: "AI handoff as a mint precondition",
    body: "Codex is the recommended client, but the protocol checks the public Skill, hashes, and handoff rather than an AI vendor brand. The EIP-712 voucher is signed only after that handoff is validated. The website can only submit what the Skill pipeline already created.",
    caveat: "The website is the wallet console, not the generator."
  },
  {
    index: "04",
    pillar: "YIELD",
    title: "Stakeless fee stream — hold the receipt, not a vault",
    body: "Most yield asks users to lock principal. CODE APE makes the NFT itself the bearer instrument of the fee stream. You do not stake, lock, or queue for unlocks. Hold the receipt while it is active; sell it and the entitlement transfers; burn it and the entitlement stops.",
    caveat: "Pending fees travel with the NFT on transfer. Always claim before listing."
  }
] as const;

const HOLD_ITEMS = [
  "Every official swap sends WETH to active NFT holders.",
  "Both directions count: WETH to CAPE and CAPE to WETH.",
  "Claim any time, or let pending fees accumulate.",
  "Keep the art and keep the fee stream."
];

const BURN_ITEMS = [
  "Burn your Ape any time to claim 5,000 CAPE base.",
  "Dynamic bonus can add more CAPE while the bonus pool lasts.",
  "Burning settles pending WETH first, then stops future NFT fees.",
  "Final decision: once burned, the NFT cannot come back."
];

const HOW_IT_WORKS = [
  ["1", "Authorize the agent", "Sign one login message so Codex or a compatible agent can create a mint session for your wallet. No ETH moves."],
  ["2", "Let Codex create", "Codex runs the Skill, creates the image, pins metadata, requests the signed voucher, and stores the handoff."],
  ["3", "Open the mint link", "The agent returns a short link like code-ape.com/mint?h=CA-XXXX. Open it to review your Ape."],
  ["4", "Choose wallet and mint", "Use All wallets, WalletConnect, or your browser wallet, then approve the final mint transaction."]
];

const FAQ = [
  ["Why start with Codex?", "Codex is the recommended client because the flow is smooth and image quality is strong today. CODE APE is still AI-agent compatible: the protocol checks the public Skill and handoff, not an AI vendor brand."],
  ["Why not generate directly on the website?", "Provenance, anti-spam, and story. Your Ape commits to a Skill version and reference pack hash, generation is gated by wallet auth, and the summon happens inside an AI-agent workflow instead of a faceless web form."],
  ["What does holding do?", "Active NFTs share the WETH fee stream from the official pool. Rewards depend on real trading volume and are never guaranteed."],
  ["What does burning do?", "Burning settles pending WETH first, then claims CAPE from the claim vault. It is final and the burned NFT no longer earns future WETH."],
  ["Is there an admin rescue button?", "No. The protocol is designed without admin pause, upgrade, LP withdraw, or hidden buyback controls. Read the proof page before minting."],
  ["Why no staking?", "Because the NFT is already the receipt. There is no separate vault, no lockup, and no unlock queue. The Uniswap v4 hook routes the official fee stream to active NFT holders. Sell the NFT and the entitlement transfers. Burn the NFT and the entitlement stops. Receipt, not key. Hold, not stake."]
];

export default function LandingPage() {
  const nftAddress = getDisplayNftAddress();
  const capeTokenAddress = getDisplayCapeTokenAddress();
  const nftExplorerUrl = getExplorerAddressUrl(nftAddress);
  const capeTokenExplorerUrl = getExplorerAddressUrl(capeTokenAddress);
  const xUrl = getCodeApeXUrl();
  const uniswapTradeUrl = getUniswapTradeUrl();

  async function copyPrompt() {
    await navigator.clipboard?.writeText(CODEX_PROMPT);
  }

  async function copyNftAddress() {
    await navigator.clipboard?.writeText(nftAddress);
  }

  async function copyCapeTokenAddress() {
    await navigator.clipboard?.writeText(capeTokenAddress);
  }

  return (
    <main>
      <section
        style={{
          minHeight: "calc(100vh - var(--nav-height))",
          display: "flex",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
          paddingTop: "var(--nav-height)",
          background: `
            radial-gradient(ellipse 80% 60% at 50% -10%, rgba(0,255,65,0.16) 0%, transparent 70%),
            radial-gradient(ellipse 50% 40% at 85% 70%, rgba(192,76,253,0.12) 0%, transparent 60%),
            var(--color-void)
          `
        }}
      >
        <div style={{ position: "absolute", inset: 0, backgroundImage: `linear-gradient(var(--color-border) 1px, transparent 1px), linear-gradient(90deg, var(--color-border) 1px, transparent 1px)`, backgroundSize: "54px 54px", opacity: 0.22 }} />

        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <TrustBar />
          <div className="launch-link-strip" aria-label="Launch links" translate="no">
            <a href={xUrl} target="_blank" rel="noreferrer" className="launch-link-icon" aria-label="Open CODE APE on X" title="Open CODE APE on X" translate="no">𝕏</a>
            <a href={uniswapTradeUrl} target="_blank" rel="noreferrer" className="launch-link-icon launch-link-icon--trade" aria-label="Open Uniswap v4 trade page" title="Open Uniswap v4 trade page" translate="no">🦄</a>
            <span className="launch-link-pill launch-link-pill--contract" translate="no">
              <a href={nftExplorerUrl} target="_blank" rel="noreferrer">NFT <code>{shortAddress(nftAddress)}</code></a>
              <button type="button" className="launch-link-copy" onClick={copyNftAddress} aria-label="Copy NFT contract address" title="Copy NFT contract address">⧉</button>
            </span>
            <span className="launch-link-pill launch-link-pill--token" translate="no">
              <a href={capeTokenExplorerUrl} target="_blank" rel="noreferrer">CA <code>{shortAddress(capeTokenAddress)}</code></a>
              <button type="button" className="launch-link-copy" onClick={copyCapeTokenAddress} aria-label="Copy CAPE token contract address" title="Copy CAPE token contract address">⧉</button>
            </span>
          </div>
          <aside className="pre-hero" aria-label="Industry pivot">
            <span className="pre-hero__muted">// 5 years of NFTs answered one question:</span>
            <span className="pre-hero__strike">who owns the JPEG.</span>
            <span className="pre-hero__arrow">→</span>
            <span className="pre-hero__accent">CODE APE answers four new ones, on one finalized contract.</span>
          </aside>
          <div className="hero-grid">
            <div>
              <div className="badge badge-prime hero-world-first" style={{ marginBottom: "var(--space-4)" }}>WORLD'S FIRST AI-SIGNED NFT</div>
              <h1 className="text-hero" style={{ marginBottom: "var(--space-5)" }}>
                Tell Codex.<br />
                <span className="gradient-text">Summon your Ape.</span>
              </h1>
              <p className="text-xl text-muted" style={{ maxWidth: 720, marginBottom: "var(--space-6)", lineHeight: 1.65 }}>
                CODE APE starts with Codex because it is the smoothest summon flow today. The protocol verifies the public Skill and handoff, not an AI brand, then your wallet signs the final mint transaction.
              </p>

              <div className="alert alert-info" style={{ marginBottom: "var(--space-6)", maxWidth: 760 }}>
                <span>🤖</span>
                <div>
                  <strong>Recommended: Codex</strong>
                  <pre className="prompt-spell" translate="no">{CODEX_PROMPT}</pre>
                  <p className="text-sm text-muted" style={{ marginTop: "var(--space-2)" }}>
                    Codex opens the Summoner Skill, creates your Ape, pins it to IPFS, and returns a short mint link. Compatible agents can do the same if they can run the Skill and call the authenticated backend flow.
                  </p>
                </div>
              </div>


              <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap", marginBottom: "var(--space-5)" }}>
                <a href={SKILL_URL} className="btn btn-primary btn-lg" id="hero-open-skill-btn" target="_blank" rel="noreferrer">🚀 Start with Codex →</a>
                <button className="btn btn-secondary btn-lg" id="hero-copy-prompt-btn" onClick={copyPrompt}>📋 Copy the Summon Spell</button>
                <Link to="/agent-auth" className="btn btn-secondary btn-lg" id="hero-mint-console-btn">✍️ AI Agent Auth</Link>
              </div>

              <div className="hero-contract-card" aria-label="NFT contract address" translate="no">
                <span>NFT contract</span>
                <a href={nftExplorerUrl} target="_blank" rel="noreferrer"><code>{shortAddress(nftAddress)}</code></a>
                <button type="button" onClick={copyNftAddress}>Copy</button>
              </div>

              <div className="stat-grid" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))", maxWidth: 760 }}>
                {STATS.map((s) => (
                  <div key={s.label} className="stat-card" style={{ padding: "var(--space-4)" }}>
                    <div className="stat-value gradient-text" style={{ fontSize: "var(--font-3xl)" }}>{s.value}</div>
                    <div className="stat-label">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

<ApeCarousel />
          </div>
        </div>
      </section>

      <section className="section" style={{ background: "var(--color-void)", paddingTop: "var(--space-12)", paddingBottom: "var(--space-12)" }}>
        <div className="container">
          <div className="firsts-section">
            <div className="firsts-header">
              <div className="eyebrow">// four firsts. one contract. zero admin.</div>
              <h2 className="firsts-title">The four new questions <span>NFTs never answered.</span></h2>
            </div>
            <div className="firsts-grid-2x2">
              {FIRSTS.map((item) => <FirstCard key={item.index} item={item} />)}
            </div>
          </div>
        </div>
      </section>

      <section className="section" style={{ background: "var(--color-surface)", paddingTop: "var(--space-12)", paddingBottom: "var(--space-12)" }}>
        <div className="container">
          <TokenomicsDiagram />
        </div>
      </section>

      <section className="section" style={{ background: "var(--color-surface)", paddingTop: "var(--space-12)", paddingBottom: "var(--space-12)" }}>
        <div className="container">
          <div style={{ textAlign: "center", marginBottom: "var(--space-8)" }}>
            <div className="badge badge-prime" style={{ marginBottom: "var(--space-3)" }}>Two strategies</div>
            <h2 className="text-4xl">Hold or burn. Decide later.</h2>
          </div>
          <div className="grid-2">
            <ChoiceCard tone="green" badge="HOLD MODE" title="💧 Hold your Ape, earn WETH" items={HOLD_ITEMS} />
            <ChoiceCard tone="red" badge="BURN MODE" title="🔥 Burn your Ape, claim CAPE" items={BURN_ITEMS} />
          </div>
        </div>
      </section>

      <section className="section" style={{ background: "var(--color-void)", paddingTop: "var(--space-12)", paddingBottom: "var(--space-12)" }}>
        <div className="container">
          <EarningsSimulator />
        </div>
      </section>

      <section id="how-it-works" className="section" style={{ background: "var(--color-void)", paddingTop: "var(--space-12)", paddingBottom: "var(--space-12)" }}>
        <div className="container">
          <div style={{ textAlign: "center", marginBottom: "var(--space-8)" }}>
            <div className="badge badge-cyan" style={{ marginBottom: "var(--space-3)" }}>How it works</div>
            <h2 className="text-4xl">From agent auth to mint in four moves</h2>
          </div>
          <div className="how-grid">
            {HOW_IT_WORKS.map(([step, title, body]) => (
              <div key={step} className="card how-card">
                <div className="badge badge-prime" style={{ marginBottom: "var(--space-3)" }}>Step {step}</div>
                <h3 className="text-xl" style={{ marginBottom: "var(--space-2)" }}>{title}</h3>
                <p className="text-sm text-muted" style={{ lineHeight: 1.65 }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" style={{ background: "var(--color-surface)", paddingTop: "var(--space-12)", paddingBottom: "var(--space-12)" }}>
        <div className="container">
          <div className="grid-2" style={{ alignItems: "start" }}>
            <div>
              <div className="badge badge-cyan" style={{ marginBottom: "var(--space-3)" }}>Plain-English proof</div>
              <h2 className="text-4xl" style={{ marginBottom: "var(--space-4)" }}>Why does this need an AI agent?</h2>
              <p className="text-muted" style={{ lineHeight: 1.7 }}>
                Because the Skill is part of the artifact. The image, metadata, signer, and reference pack are all tied into a handoff that your wallet can verify before minting.
              </p>
            </div>
            <div style={{ display: "grid", gap: "var(--space-3)" }}>
              {FAQ.map(([question, answer]) => (
                <details key={question} className="faq-item">
                  <summary>{question}</summary>
                  <p>{answer}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section" style={{ background: "var(--color-void)", paddingTop: "var(--space-12)", paddingBottom: "var(--space-12)" }}>
        <div className="container">
          <blockquote className="manifesto-callout">
            <p>We believe NFT mints should be summoned, not clicked.</p>
            <p>We believe holders should earn from the pool they helped create, not depend on team treasury.</p>
            <p>We believe code is the only authority that does not lie.</p>
          </blockquote>
          <div style={{ textAlign: "center", marginTop: "var(--space-5)" }}>
            <Link to="/manifesto" className="btn btn-secondary">Read the manifesto →</Link>
          </div>
        </div>
      </section>

      <section className="section" style={{ background: "var(--color-surface)", paddingTop: "var(--space-12)", paddingBottom: "var(--space-12)" }}>
        <div className="container">
          <div className="alert alert-warning" style={{ maxWidth: 1000, margin: "0 auto" }}>
            <span style={{ fontSize: "1.5rem" }}>⚠️</span>
            <div>
              <strong>Mint notes</strong>
              <ul className="risk-list">
                <li>Once minting is complete, no admin can pause it.</li>
                <li>You earn WETH only while swaps happen on the official pool. We do not guarantee swap volume.</li>
                <li>Sell-side swaps receive less WETH than no-hook quotes because the holder fee is collected.</li>
                <li>After launch, initial liquidity auto-locks into UNCX V4 before trading opens. Public proof shows the lock evidence and owner.</li>
                <li>Pending WETH belongs to whoever owns the NFT right now. If you list on a marketplace, claim first.</li>
              </ul>
              <Link to="/proof" style={{ color: "var(--color-gold)", marginTop: "var(--space-3)", display: "inline-block" }}>Full proof page →</Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function FirstCard({ item }: { item: (typeof FIRSTS)[number] }) {
  return (
    <article className="card first-card first-card--large">
      <header className="first-card__head">
        <span className="first-num">{item.index}</span>
        <span className="first-card__pillar">{item.pillar}</span>
      </header>
      <h3 className="text-xl">{item.title}</h3>
      <p className="text-sm text-muted first-card__body">{item.body}</p>
      <p className="first-card__caveat">{item.caveat}</p>
    </article>
  );
}

function ChoiceCard({ tone, badge, title, items }: { tone: "green" | "red"; badge: string; title: string; items: string[] }) {
  return (
    <div className={`card choice-card choice-card--${tone}`} style={{ padding: "var(--space-6)" }}>
      <div className={`badge ${tone === "green" ? "badge-green" : "badge-red"}`} style={{ marginBottom: "var(--space-3)" }}>{badge}</div>
      <h3 className="text-2xl" style={{ marginBottom: "var(--space-4)" }}>{title}</h3>
      <ul style={{ display: "grid", gap: "var(--space-3)", color: "var(--text-secondary)", lineHeight: 1.65 }}>
        {items.map((item) => <li key={item}>{item}</li>)}
      </ul>
    </div>
  );
}
